import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';
import debounce from 'lodash/debounce';
import usePageTitle from '../../hooks/usePageTitle';

const BOMComponentSwap = () => {
  // Source item state
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceItem, setSourceItem] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);

  // Destination item state
  const [destSearch, setDestSearch] = useState('');
  const [destOptions, setDestOptions] = useState([]);
  const [destItem, setDestItem] = useState(null);
  const [destLoading, setDestLoading] = useState(false);

  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Swap state
  const [swapping, setSwapping] = useState(false);
  const [lastSwap, setLastSwap] = useState(null);

  usePageTitle('BOM Component Swap');

  // Debounced search for source items
  const searchSourceItems = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setSourceOptions([]);
        return;
      }
      setSourceLoading(true);
      try {
        const response = await manufacturingAPI.items.list({ search: query, page_size: 20 });
        setSourceOptions(response.data.results || []);
      } catch (error) {
        console.error('Error searching items:', error);
      } finally {
        setSourceLoading(false);
      }
    }, 300),
    []
  );

  // Debounced search for destination items
  const searchDestItems = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setDestOptions([]);
        return;
      }
      setDestLoading(true);
      try {
        const response = await manufacturingAPI.items.list({ search: query, page_size: 20 });
        setDestOptions(response.data.results || []);
      } catch (error) {
        console.error('Error searching items:', error);
      } finally {
        setDestLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    searchSourceItems(sourceSearch);
  }, [sourceSearch, searchSourceItems]);

  useEffect(() => {
    searchDestItems(destSearch);
  }, [destSearch, searchDestItems]);

  // Auto-preview when both items are selected
  useEffect(() => {
    if (sourceItem && destItem && sourceItem.id !== destItem.id) {
      handlePreview();
    } else if (sourceItem && !destItem) {
      // Preview just source to show affected BOMs
      handlePreview();
    } else {
      setPreview(null);
    }
  }, [sourceItem, destItem]);

  const handlePreview = async () => {
    if (!sourceItem) return;

    setPreviewLoading(true);
    try {
      const response = await manufacturingAPI.bomSwap.validate({
        source_item_id: sourceItem.id,
        destination_item_id: destItem?.id || null,
      });
      setPreview(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Preview failed';
      toast.error(errorMsg);
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSwap = async () => {
    if (!sourceItem || !destItem) {
      toast.error('Please select both source and destination items');
      return;
    }

    if (sourceItem.id === destItem.id) {
      toast.error('Source and destination items must be different');
      return;
    }

    if (preview?.conflicts?.length > 0) {
      toast.error('Cannot swap - conflicts exist');
      return;
    }

    setSwapping(true);
    try {
      const response = await manufacturingAPI.bomSwap.execute({
        source_item_id: sourceItem.id,
        destination_item_id: destItem.id,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setLastSwap(response.data);

        // Reset form
        setSourceItem(null);
        setSourceSearch('');
        setDestItem(null);
        setDestSearch('');
        setPreview(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Swap failed';
      toast.error(errorMsg);
    } finally {
      setSwapping(false);
    }
  };

  const handleReset = () => {
    setSourceItem(null);
    setSourceSearch('');
    setDestItem(null);
    setDestSearch('');
    setPreview(null);
    setLastSwap(null);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapIcon fontSize="large" />
        BOM Component Swap
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Replace a component in all BOMs with a different item. This will swap the source item with the destination item wherever it appears as a component, keeping quantities the same.
      </Typography>

      {lastSwap && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setLastSwap(null)}
          icon={<CheckCircleIcon />}
        >
          <Typography variant="subtitle2">
            Swap completed successfully
          </Typography>
          <Typography variant="body2">
            Replaced <strong>{lastSwap.source_item}</strong> with <strong>{lastSwap.destination_item}</strong> in {lastSwap.swapped_count} BOMs
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          {/* Source Section */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                  Find Component (Source)
                </Typography>

                <Autocomplete
                  options={sourceOptions}
                  getOptionLabel={(option) => `${option.item_code} - ${option.name}`}
                  value={sourceItem}
                  onChange={(_, newValue) => setSourceItem(newValue)}
                  inputValue={sourceSearch}
                  onInputChange={(_, newValue) => setSourceSearch(newValue)}
                  loading={sourceLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Item to Replace"
                      placeholder="Type to search..."
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {sourceLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />

                {sourceItem && (
                  <Box sx={{ p: 1.5, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {sourceItem.item_code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sourceItem.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={sourceItem.item_type}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Arrow */}
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SwapIcon sx={{ fontSize: 48, color: 'primary.main', transform: { xs: 'rotate(90deg)', md: 'rotate(0deg)' } }} />
          </Grid>

          {/* Destination Section */}
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
                  Replace With (Destination)
                </Typography>

                <Autocomplete
                  options={destOptions}
                  getOptionLabel={(option) => `${option.item_code} - ${option.name}`}
                  value={destItem}
                  onChange={(_, newValue) => setDestItem(newValue)}
                  inputValue={destSearch}
                  onInputChange={(_, newValue) => setDestSearch(newValue)}
                  loading={destLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Replacement Item"
                      placeholder="Type to search..."
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {destLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />

                {destItem && (
                  <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {destItem.item_code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {destItem.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={destItem.item_type}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={swapping}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={handleSwap}
                disabled={
                  swapping ||
                  !sourceItem ||
                  !destItem ||
                  sourceItem?.id === destItem?.id ||
                  preview?.conflicts?.length > 0 ||
                  preview?.affected_count === 0
                }
                startIcon={swapping ? <CircularProgress size={20} /> : <SwapIcon />}
                color="primary"
              >
                {swapping ? 'Swapping...' : `Swap in ${preview?.affected_count || 0} BOMs`}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Preview Section */}
      {previewLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {preview && !previewLoading && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon />
            Preview: {preview.affected_count} BOMs Affected
          </Typography>

          {preview.conflicts?.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
              <Typography variant="subtitle2">Conflicts Detected</Typography>
              <Typography variant="body2">
                The following BOMs already contain the destination item:
              </Typography>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                {preview.conflicts.map((conflict, idx) => (
                  <li key={idx}>{conflict.message}</li>
                ))}
              </ul>
            </Alert>
          )}

          {preview.affected_count === 0 ? (
            <Alert severity="info">
              No BOMs found containing <strong>{sourceItem?.item_code}</strong> as a component.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Parent Item</TableCell>
                    <TableCell>Parent Name</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>Operation</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.affected_boms.map((bom) => (
                    <TableRow
                      key={bom.bom_id}
                      sx={{
                        bgcolor: bom.has_conflict ? 'error.50' : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {bom.parent_item_code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {bom.parent_item_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {bom.quantity_required}
                      </TableCell>
                      <TableCell>
                        {bom.operation || '-'}
                      </TableCell>
                      <TableCell>
                        {bom.has_conflict ? (
                          <Chip
                            size="small"
                            label="Conflict"
                            color="error"
                            icon={<WarningIcon />}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="Will Swap"
                            color="success"
                            icon={<CheckCircleIcon />}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default BOMComponentSwap;